/**
 * BookmarksPage — Sammlungen des Users, zwei Tabs:
 *   1) Gespeicherte Beiträge (Saved Posts)
 *   2) Meine gespeicherten Suchen (Search Alerts)
 * iter121: Route `/bookmarks` konsolidiert die zwei bereits vorhandenen Endpoints.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bookmark, Bell, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { key: "posts", labelKey: "bookmarks.tab_posts", labelDefault: "Gespeicherte Beiträge", icon: Bookmark, testId: "bookmarks-tab-posts" },
  { key: "searches", labelKey: "bookmarks.tab_searches", labelDefault: "Meine gespeicherten Suchen", icon: Bell, testId: "bookmarks-tab-searches" },
];

export default function BookmarksPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = TABS.some((tb) => tb.key === searchParams.get("tab")) ? searchParams.get("tab") : "posts";
  const setActive = (k) => {
    const p = new URLSearchParams(searchParams);
    if (k === "posts") p.delete("tab"); else p.set("tab", k);
    setSearchParams(p, { replace: true });
  };

  const [posts, setPosts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      const [p, a] = await Promise.allSettled([
        api.get("/profile/watchlist/enhanced?area=SOCIAL"),
        api.get("/search-alerts"),
      ]);
      if (p.status === "fulfilled") setPosts(p.value.data?.listings || []);
      if (a.status === "fulfilled") setAlerts(a.value.data?.alerts || a.value.data || []);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    load();
  }, [user, navigate, load]);

  return (
    <div data-testid="bookmarks-page" className="w-full max-w-3xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0 py-6">
      <h1 className="text-2xl font-bold text-[#0A0A0A] px-4 mb-4 flex items-center gap-2">
        <Bookmark size={22} /> {t("bookmarks.title", { defaultValue: "Sammlungen" })}
      </h1>

      {/* Tabs */}
      <div className="flex gap-2 px-4 border-b border-[#E5E5E5] mb-4">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          const isActive = active === tb.key;
          return (
            <button
              key={tb.key}
              data-testid={tb.testId}
              onClick={() => setActive(tb.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                isActive ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#666] hover:text-[#0A0A0A]"
              }`}
            >
              <Icon size={16} /> {t(tb.labelKey, { defaultValue: tb.labelDefault })}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#999]" /></div>
      ) : active === "posts" ? (
        posts.length === 0 ? (
          <p data-testid="bookmarks-posts-empty" className="text-center text-sm text-[#999] py-16">{t("bookmarks.empty_posts", { defaultValue: "Noch keine gespeicherten Beiträge." })}</p>
        ) : (
          <ul className="grid gap-3 px-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}>
            {posts.map((p) => (
              <li key={p.listing_id || p.post_id}>
                <button
                  data-testid={`bookmark-post-${p.listing_id || p.post_id}`}
                  onClick={() => {
                    if (p.link_path) return navigate(p.link_path);
                    const h = p.author?.handle;
                    const pid = p.post_id || p.listing_id;
                    navigate(h ? `/user/${h}?post=${pid}` : "/");
                  }}
                  className="block w-full text-left border border-[#E5E5E5] rounded-2xl overflow-hidden hover:border-[#0A0A0A] transition-colors"
                >
                  <div className="aspect-square bg-[#F5F5F5]">
                    {(p.media_urls?.[0] || p.thumbnail_url) && (
                      <img src={p.media_urls?.[0] || p.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-[#0A0A0A] line-clamp-1">{p.title || p.text || "—"}</p>
                    {p.author?.display_name && <p className="text-xs text-[#666] mt-0.5">{p.author.display_name}</p>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : (
        alerts.length === 0 ? (
          <p data-testid="bookmarks-searches-empty" className="text-center text-sm text-[#999] py-16">{t("bookmarks.empty_searches", { defaultValue: "Keine gespeicherten Suchen." })}</p>
        ) : (
          <ul className="flex flex-col gap-2 px-4">
            {alerts.map((a) => (
              <li key={a.alert_id || a.id}>
                <button
                  data-testid={`bookmark-search-${a.alert_id || a.id}`}
                  onClick={() => navigate(`/search?${new URLSearchParams(a.filters || {}).toString()}`)}
                  className="w-full flex items-center gap-3 border border-[#E5E5E5] rounded-xl p-3 hover:bg-[#FAFAFA] transition-colors text-left"
                >
                  <Bell size={18} className="text-[#666] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A0A0A] truncate">{a.name || a.query || "Gespeicherte Suche"}</p>
                    {a.filters && <p className="text-xs text-[#999] truncate">{Object.entries(a.filters).map(([k, v]) => `${k}=${v}`).join(" · ")}</p>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
